// ui/skillProgress.js
// Centralized skill progress calculation for compact headers

import { gameState } from '../game/gameState.js';
import { GAME_CONFIG } from '../game/constants.js';
import { formatWithCommas } from '../utils/format.js';

/**
 * Calculate skill progress info for UI display
 * Handles regular levels, max level, and virtual levels
 *
 * @param {string} skillId - The skill ID (from SKILLS enum)
 * @returns {{ levelDisplay: string|number, progressPercent: number, progressText: string }}
 */
export function getSkillProgressInfo(skillId) {
    const skill = gameState.getSkill(skillId);

    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = skill.nextLevelXp > 0
            ? (skill.currentXp / skill.nextLevelXp * 100)
            : 0;
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    return { levelDisplay, progressPercent, progressText };
}

/**
 * Update compact header progress elements
 *
 * @param {string} containerSelector - CSS selector for the interface container (e.g., '.mining-interface')
 * @param {string} skillId - The skill ID
 */
export function updateCompactHeaderProgress(containerSelector, skillId) {
    const { progressPercent, progressText } = getSkillProgressInfo(skillId);

    const progressFill = document.querySelector(`${containerSelector} .compact-progress-fill`);
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector(`${containerSelector} .compact-progress-percent`);
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector(`${containerSelector} .compact-progress-text`);
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;
}

export default {
    getSkillProgressInfo,
    updateCompactHeaderProgress
};
